import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { impactOccuer } from '../utils/hapticEvents';

const PULL_THRESHOLD = 110;
const MAX_PULL = 150;
const RESISTANCE = 0.45;
const RELOAD_DELAY = 500;
// Icon + gap + text + bottom padding — how tall the indicator's own content is.
const INDICATOR_CONTENT_HEIGHT = 48;
// Always leave at least this much clearance at the top, even on pages with no header of their own.
const BASE_TOP_MARGIN = 12;

type PullToRefreshProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  /** Extra space (px) to leave clear at the top, e.g. for a page's own fixed header. */
  topOffset?: number;
};

const findScrollTarget = (start: EventTarget | null, boundary: HTMLElement) => {
  let node = start instanceof Element ? start : null;

  while (node && node !== boundary) {
    const canScrollY =
      node.scrollHeight > node.clientHeight &&
      /(auto|scroll)/.test(getComputedStyle(node).overflowY);

    if (canScrollY) return node;
    node = node.parentElement;
  }

  return boundary;
};

export const PullToRefresh = ({
  children,
  className,
  style,
  id,
  topOffset = 0,
}: PullToRefreshProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const scrollTargetRef = useRef<Element | null>(null);
  const pullingRef = useRef(false);
  const pullRef = useRef(0);
  const readyRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [ready, setReady] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || refreshing) return;

    const reset = () => {
      pullingRef.current = false;
      startYRef.current = null;
      scrollTargetRef.current = null;
      pullRef.current = 0;
      readyRef.current = false;
      setPull(0);
      setReady(false);
    };

    const onTouchStart = (e: TouchEvent) => {
      const touchTarget = e.target instanceof Element ? e.target : null;

      if (touchTarget?.closest('[data-blocks-pull-refresh]')) {
        startYRef.current = null;
        pullingRef.current = false;
        return;
      }

      const target = findScrollTarget(e.target, el);

      if (target.scrollTop <= 0) {
        scrollTargetRef.current = target;
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
      } else {
        startYRef.current = null;
        pullingRef.current = false;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || startYRef.current === null || !scrollTargetRef.current) return;

      const delta = e.touches[0].clientY - startYRef.current;

      if (delta <= 0 || scrollTargetRef.current.scrollTop > 0) {
        reset();
        return;
      }

      e.preventDefault();
      const damped = Math.min(delta * RESISTANCE, MAX_PULL);
      pullRef.current = damped;
      setPull(damped);

      const nowReady = damped >= PULL_THRESHOLD;
      if (nowReady !== readyRef.current) {
        readyRef.current = nowReady;
        setReady(nowReady);
        impactOccuer('rigid');
      }
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      startYRef.current = null;

      if (pullRef.current < PULL_THRESHOLD) {
        reset();
        return;
      }

      setReady(false);
      setRefreshing(true);
      setTimeout(() => window.location.reload(), RELOAD_DELAY);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [refreshing]);

  const effectiveTopOffset = topOffset + BASE_TOP_MARGIN;
  const displayPull = refreshing ? effectiveTopOffset + INDICATOR_CONTENT_HEIGHT : pull;
  const contentReveal = Math.max(0, displayPull - effectiveTopOffset);

  return (
    <div ref={scrollRef} id={id} className={className} style={style}>
      <div
        className="flex shrink-0 flex-col justify-end overflow-hidden"
        style={{
          height: displayPull,
          transition: pullingRef.current ? 'none' : 'height 0.25s ease-out',
        }}
      >
        <div
          className="flex flex-col items-center justify-end overflow-hidden"
          style={{
            height: contentReveal,
            transition: pullingRef.current ? 'none' : 'height 0.25s ease-out',
          }}
        >
          <div className="flex flex-col items-center gap-1 pb-3">
            <Loader2
              className={cn('h-5 w-5 text-brand-green', refreshing && 'animate-spin')}
              style={
                refreshing
                  ? undefined
                  : { transform: `rotate(${Math.min((pull / PULL_THRESHOLD) * 180, 180)}deg)` }
              }
            />
            <span className="whitespace-nowrap text-[11px] text-text-tertiary">
              {refreshing
                ? 'Обновление...'
                : ready
                  ? 'Отпустите для обновления'
                  : 'Потяните для обновления'}
            </span>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
